import Icon from '../components/Icon';
// src/pages/ReceivePage.js
// Public page — no auth required. Handles the OTP verification flow.

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { formatBytes, formatDate } from '../utils/api';
import JSZip from 'jszip';

const BASE = '/api';

async function apiCall(path, options = {}) {
  const res = await fetch(`${BASE}/${path}`, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

// ── OTP input — 6 boxes ───────────────────────────────────────────────────────
function OTPBoxes({ onComplete }) {
  const [digits, setDigits] = useState(Array(6).fill(''));
  const refs = useRef([]);

  const onChange = (i, val) => {
    const d = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = d;
    setDigits(next);
    if (d && i < 5) refs.current[i + 1]?.focus();
    if (next.every(x => x)) onComplete(next.join(''));
  };

  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const onPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      onComplete(pasted);
    }
  };

  return (
    <div className="otp-digit-row">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => refs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={e => onChange(i, e.target.value)}
          onKeyDown={e => onKeyDown(i, e)}
          onPaste={onPaste}
          className={`otp-digit${d ? ' filled' : ''}`}
        />
      ))}
    </div>
  );
}

export default function ReceivePage() {
  const { token } = useParams();
  const [transfer, setTransfer] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [stage, setStage] = useState('loading'); // loading | password | request-otp | verify-otp | access | error | expired
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otpError, setOtpError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [session, setSession] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [viewingPdf, setViewingPdf] = useState(null);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [credentialContent, setCredentialContent] = useState(null);
  const [credentialLoading, setCredentialLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const data = await apiCall(`otp/transfer/${token}`);
        setTransfer(data);
        setStage(data.hasPassword ? 'password' : 'request-otp');
      } catch (e) {
        setLoadError(e.message);
        setStage(e.message.includes('expired') || e.message.includes('used') ? 'expired' : 'error');
      }
    }
    load();
  }, [token]);

  const verifyPassword = async () => {
    setPasswordError('');
    try {
      await apiCall(`otp/verify-password/${token}`, {
        method: 'POST',
        body: JSON.stringify({ password: passwordInput }),
      });
      setStage('request-otp');
    } catch (e) {
      setPasswordError(e.message);
    }
  };

  const requestOTP = async () => {
    try {
      const data = await apiCall(`otp/request/${token}`, { method: 'POST', body: '{}' });
      setMaskedEmail(data.maskedEmail);
      setOtpSent(true);
      setStage('verify-otp');
    } catch (e) {
      setOtpError(e.message);
    }
  };

  const verifyOTP = async (code) => {
    setVerifying(true);
    setOtpError('');
    try {
      const data = await apiCall(`otp/verify/${token}`, {
        method: 'POST',
        body: JSON.stringify({ code, password: passwordInput }),
      });
      setSession(data);
      setStage('access');
      // If this is a credential transfer, auto-load the content
      if (transfer?.transferType === 'credential') {
        setCredentialLoading(true);
        try {
          const cred = await apiCall(`otp/credential/${token}`, {
            headers: { 'x-session-token': data.sessionToken },
          });
          setCredentialContent(cred.content);
        } catch (e) {
          setCredentialContent('Error loading credentials: ' + e.message);
        } finally {
          setCredentialLoading(false);
        }
      }
    } catch (e) {
      if (transfer?.hasPassword && /password/i.test(e.message) && !/too many/i.test(e.message)) {
        // The password was wrong or has been changed by the sender — ask for it again
        setPasswordInput('');
        setPasswordError('That password is no longer valid. Please enter the current password.');
        setStage('password');
      } else {
        setOtpError(e.message);
      }
    } finally {
      setVerifying(false);
    }
  };

  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');

  const handleDownloadAll = async () => {
    const fileNames = transfer?.fileNames || [];
    if (!fileNames.length) return;
    setDownloadingAll(true);
    setDownloadProgress('Preparing download…');

    try {
      const data = await apiCall(`otp/download-all/${token}`, {
        headers: { 'x-session-token': session.sessionToken },
      });

      if (fileNames.length === 1) {
        // Single file — direct download
        setDownloadProgress('Downloading…');
        const a = document.createElement('a');
        a.href = data.files[0].url;
        a.download = data.files[0].name;
        a.click();
      } else {
        // Multiple files — zip them client-side
        const zip = new JSZip();
        let i = 0;
        for (const file of data.files) {
          i++;
          setDownloadProgress(`Downloading file ${i} of ${data.files.length}…`);
          const resp = await fetch(file.url);
          const blob = await resp.blob();
          zip.file(file.name, blob);
        }
        setDownloadProgress('Creating ZIP…');
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(zipBlob);
        a.download = `${data.title || 'transfer'}.zip`;
        a.click();
        URL.revokeObjectURL(a.href);
      }

      setDownloadComplete(true);
      setDownloadProgress('');
    } catch (e) {
      alert('Download failed: ' + e.message);
      setDownloadProgress('');
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDownload = async (filename) => {
    // View-only mode (PDFs) — kept for view-only transfers
    if (transfer?.viewOnly) {
      try {
        setDownloading(d => ({ ...d, [filename]: true }));
        const data = await apiCall(`otp/download/${token}/${encodeURIComponent(filename)}`, {
          headers: { 'x-session-token': session.sessionToken },
        });
        setViewingPdf(data.viewUrl);
      } catch (e) {
        alert('Failed to load file: ' + e.message);
      } finally {
        setDownloading(d => ({ ...d, [filename]: false }));
      }
    }
  };

  const Header = () => (
    <div className="receive-header">
      <div className="receive-crest">
        <Icon size={36} />
      </div>
      <div style={{ lineHeight: 1.2 }}>
        <div className="receive-brand">SUFC SECURE TRANSFER SYSTEM</div>
      </div>
    </div>
  );

  if (stage === 'password') {
    return (
      <div className="receive-page">
        <div className="receive-card">
          <Header />
          <div className="receive-body">
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '40px', marginBottom: '10px' }}>🔑</div>
              <h2 className="receive-title">Password Required</h2>
              <p className="receive-from">This transfer is password protected. Enter the password provided by the sender.</p>
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                className="form-input"
                type="password"
                placeholder="Enter transfer password"
                value={passwordInput}
                onChange={e => setPasswordInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && verifyPassword()}
                autoFocus
              />
            </div>
            {passwordError && <div className="alert alert-error" style={{ marginBottom: '12px' }}>❌ {passwordError}</div>}
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
              onClick={verifyPassword} disabled={!passwordInput.trim()}>
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'loading') {
    return (
      <div className="receive-page">
        <div className="receive-card">
          <Header />
          <div className="receive-body" style={{ textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '32px auto' }} />
            <p>Loading transfer…</p>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'expired' || stage === 'error') {
    return (
      <div className="receive-page">
        <div className="receive-card">
          <Header />
          <div className="receive-body" style={{ textAlign: 'center', padding: '40px 28px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {stage === 'expired' ? '🔗' : '🔍'}
            </div>
            <h2 style={{ color: 'var(--navy)', marginBottom: '8px' }}>
              {stage === 'expired' ? 'This link has expired' : 'Link not found'}
            </h2>
            <p style={{ color: 'var(--grey-500)', fontSize: '14px', lineHeight: '1.6' }}>
              {loadError || 'This secure link is no longer valid. It may have been used, expired, or the sender may have regenerated it.'}
            </p>
            <p style={{ color: 'var(--grey-400)', fontSize: '13px', marginTop: '16px' }}>
              If you believe this is an error, please contact the person who shared the files with you.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'request-otp') {
    return (
      <div className="receive-page">
        <div className="receive-card">
          <Header />
          <div className="receive-body">
            <h2 className="receive-title">{transfer?.title}</h2>
            <p className="receive-from">Shared by {transfer?.uploaderName}</p>

            {transfer?.viewOnly && (
              <div className="alert alert-info" style={{ marginBottom: '16px' }}>
                👁 These files are shared in <strong>view-only</strong> mode. You can view them in your browser but not download them.
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              {transfer?.transferType === 'credential' ? (
                <div className="alert alert-warn">
                  🔐 You have been sent secure credentials. After verifying your identity, the content will be displayed <strong>once only</strong> and then permanently deleted.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: '12px', color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                    {transfer?.fileNames?.length} file{transfer?.fileNames?.length !== 1 ? 's' : ''} shared
                  </div>
                  {transfer?.fileNames?.map((name, i) => (
                    <div key={i} className="file-item" style={{ marginBottom: '6px' }}>
                      <span style={{ fontSize: '18px' }}>📄</span>
                      <span className="file-item-name">{name}</span>
                      <span className="file-item-size">{formatBytes(transfer.fileSizes?.[i])}</span>
                    </div>
                  ))}
                </>
              )}
            </div>

            <div className="alert alert-warn">
              🔐 To access these files, we need to verify your identity. We'll send a one-time code to the email address this was shared with.
            </div>

            {otpError && <div className="alert alert-error">{otpError}</div>}

            <p style={{ fontSize: '13px', color: 'var(--grey-500)', marginBottom: '16px' }}>
              Expires: {formatDate(transfer?.expiresAt)}
            </p>

            <button className="btn btn-primary btn-lg" style={{ width: '100%' }} onClick={requestOTP}>
              Send verification code to my email
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'verify-otp') {
    return (
      <div className="receive-page">
        <div className="receive-card">
          <Header />
          <div className="receive-body">
            <h2 className="receive-title">Check your email</h2>
            <p className="receive-from">We sent a 6-digit code to {maskedEmail}</p>

            <p style={{ fontSize: '14px', color: 'var(--grey-600)', margin: '16px 0' }}>
              Enter the code below. It expires in <strong>10 minutes</strong>.
            </p>

            {otpError && <div className="alert alert-error">❌ {otpError}</div>}

            {verifying ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div className="spinner" style={{ margin: '0 auto 12px' }} />
                <p>Verifying…</p>
              </div>
            ) : (
              <OTPBoxes onComplete={verifyOTP} />
            )}

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                style={{ background: 'none', border: 'none', color: 'var(--grey-500)', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}
                onClick={() => { setOtpError(''); requestOTP(); }}
              >
                Resend code
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'access') {
    // ── Credential view ──
    if (transfer?.transferType === 'credential') {
      return (
        <div className="receive-page">
          <div className="receive-card">
            <Header />
            <div className="receive-body">
              <h2 className="receive-title">✅ Identity verified</h2>
              <p className="receive-from">Shared by {transfer?.uploaderName}</p>

              {credentialLoading ? (
                <div style={{ textAlign: 'center', padding: '32px' }}>
                  <div className="spinner" style={{ margin: '0 auto 12px' }} />
                  <p>Loading credentials…</p>
                </div>
              ) : (
                <>
                  <div className="alert alert-warn" style={{ marginBottom: '16px' }}>
                    ⚠️ <strong>These credentials have now expired.</strong> This link cannot be used again. Save the information below immediately.
                  </div>
                  <div style={{ background: 'var(--g900)', padding: '16px', marginBottom: '12px', position: 'relative' }}>
                    <pre style={{ color: '#00ff88', fontFamily: 'var(--font-mono)', fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
                      {credentialContent}
                    </pre>
                  </div>
                  <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => {
                      navigator.clipboard.writeText(credentialContent || '');
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}>
                    {copied ? '✅ Copied!' : '📋 Copy to clipboard'}
                  </button>
                </>
              )}
              <p style={{ fontSize: '12px', color: 'var(--g400)', marginTop: '16px', textAlign: 'center' }}>
                🔒 Your access has been logged for security purposes.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // ── File download view ──
    return (
      <div className="receive-page">
        {viewingPdf && (
          <div className="modal-overlay" onClick={() => setViewingPdf(null)}>
            <div style={{ width: '90vw', height: '90vh', background: 'white', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '12px 16px', background: 'var(--navy)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'white', fontWeight: 600 }}>Document Viewer — View Only</span>
                <button style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }} onClick={() => setViewingPdf(null)}>×</button>
              </div>
              <iframe src={viewingPdf} className="pdf-viewer" title="PDF Viewer" />
            </div>
          </div>
        )}

        <div className="receive-card">
          <Header />
          <div className="receive-body">
            <h2 className="receive-title">✅ Identity verified</h2>
            <p className="receive-from">
              Shared by {transfer?.uploaderName}
            </p>

            {downloadComplete ? (
              <div className="alert alert-success" style={{ marginBottom: '16px' }}>✅ Download started. This link has now expired.</div>
            ) : (
              <>
                {/* File list — info only */}
                <div className="file-download-list" style={{ marginBottom: '20px' }}>
                  {transfer?.fileNames?.map((name, i) => (
                    <div key={i} className="download-item">
                      <div className="download-item-info">
                        <div className="download-item-name">📄 {name}</div>
                        <div className="download-item-size">{formatBytes(transfer.fileSizes?.[i])}</div>
                      </div>
                      {transfer?.viewOnly && (
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => handleDownload(name)}
                          disabled={!!downloading[name]}>
                          {downloading[name] ? '…' : '👁 View'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Single download button */}
                {!transfer?.viewOnly && (
                  <button className="btn btn-primary btn-lg"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={handleDownloadAll}
                    disabled={downloadingAll}>
                    {downloadingAll
                      ? (downloadProgress || 'Preparing…')
                      : transfer?.fileNames?.length > 1
                        ? `⬇️ Download all ${transfer.fileNames.length} files as ZIP`
                        : '⬇️ Download file'}
                  </button>
                )}
              </>
            )}

            <p style={{ fontSize: '12px', color: 'var(--grey-400)', marginTop: '16px', textAlign: 'center' }}>
              🔒 Your access is being logged for security purposes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
